/**
 * PhaseSyncTimeline Component
 *
 * A timeline component for phase-aligned video synchronization.
 * Designed for comparing template vs athlete video phases.
 *
 * Features:
 * - Phase selector buttons for navigation
 * - Progress slider within current phase (0-100%)
 * - Timing comparison display (template vs athlete duration)
 * - Timing indicator (slower/faster/on-pace)
 * - Current phase highlighting
 */

import React from 'react';
import type { PhaseMapping } from '@/types/api';
import { formatScore } from '@/utils/formatters';

interface PhaseSyncTimelineProps {
  phaseMapping: PhaseMapping | null | undefined;
  currentPhaseIndex: number;
  phaseProgress: number; // 0.0 - 1.0
  templateFps?: number;
  athleteFps?: number;
  onPhaseSelect: (index: number) => void;
  onProgressChange: (progress: number) => void;
  className?: string;
}

// Color mapping by importance level
const IMPORTANCE_COLORS: Record<string, string> = {
  critical: '#ef4444', // red-500
  high: '#f97316',     // orange-500
  medium: '#3b82f6',   // blue-500
  low: '#9ca3af',      // gray-400
};

// Format duration in seconds
const formatDuration = (frames: number, fps: number): string => {
  const seconds = frames / fps;
  return `${seconds.toFixed(1)}s`;
};

// Get timing indicator based on ratio
const getTimingInfo = (ratio: number): { indicator: string; color: string; label: string } => {
  if (ratio > 1.1) {
    return { indicator: '🐌', color: 'text-amber-600', label: 'Slower' };
  }
  if (ratio < 0.9) {
    return { indicator: '⚡', color: 'text-blue-600', label: 'Faster' };
  }
  return { indicator: '✓', color: 'text-green-600', label: 'On pace' };
};

export const PhaseSyncTimeline: React.FC<PhaseSyncTimelineProps> = ({
  phaseMapping,
  currentPhaseIndex,
  phaseProgress,
  templateFps = 30,
  athleteFps = 30,
  onPhaseSelect,
  onProgressChange,
  className = '',
}) => {
  const phasePairs = phaseMapping?.phase_pairs || [];
  const currentPair = phasePairs[currentPhaseIndex] || null;

  if (!phaseMapping || phasePairs.length === 0) {
    return (
      <div className={`bg-gray-800 rounded-lg p-4 ${className}`}>
        <div className="text-gray-400 text-sm text-center">
          No phase data available
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-800 rounded-lg p-4 ${className}`}>
      {/* Phase selector buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {phasePairs.map((pair, index) => {
          const isSelected = index === currentPhaseIndex;
          const color = IMPORTANCE_COLORS[pair.template_phase.importance] || IMPORTANCE_COLORS.medium;

          return (
            <button
              key={pair.template_phase.phase_id}
              onClick={() => onPhaseSelect(index)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                isSelected
                  ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800'
                  : 'hover:opacity-80'
              }`}
              style={{
                backgroundColor: color,
                opacity: isSelected ? 1 : 0.7,
              }}
            >
              {pair.template_phase.label}
            </button>
          );
        })}
      </div>

      {/* Current phase info */}
      {currentPair && (
        <div className="bg-gray-700 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h4 className="text-white font-medium">
                {currentPair.template_phase.label}
              </h4>
              <p className="text-gray-400 text-sm">
                Phase {currentPhaseIndex + 1} of {phasePairs.length}
              </p>
            </div>
            <div className="text-right">
              <div className={`text-lg font-bold ${getTimingInfo(currentPair.timing_ratio).color}`}>
                {getTimingInfo(currentPair.timing_ratio).indicator}{' '}
                {getTimingInfo(currentPair.timing_ratio).label}
              </div>
              <div className="text-gray-400 text-sm">
                {formatScore(currentPair.timing_ratio)} of template time
              </div>
            </div>
          </div>

          {/* Duration comparison */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-600 rounded p-3">
              <div className="text-gray-400 text-xs mb-1">Template Duration</div>
              <div className="text-white font-medium">
                {formatDuration(currentPair.template_phase.duration_frames, templateFps)}
              </div>
              <div className="text-gray-500 text-xs">
                Frames {currentPair.template_phase.start_frame} - {currentPair.template_phase.end_frame}
              </div>
            </div>
            <div className="bg-gray-600 rounded p-3">
              <div className="text-gray-400 text-xs mb-1">Athlete Duration</div>
              <div className="text-white font-medium">
                {formatDuration(currentPair.athlete_phase.duration_frames, athleteFps)}
              </div>
              <div className="text-gray-500 text-xs">
                Frames {currentPair.athlete_phase.start_frame} - {currentPair.athlete_phase.end_frame}
              </div>
            </div>
          </div>

          {/* Scores */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-600 rounded p-2">
              <div className="text-gray-400 text-xs">Timing Score</div>
              <div className="text-white font-medium">
                {formatScore(currentPair.timing_score)}
              </div>
            </div>
            <div className="bg-gray-600 rounded p-2">
              <div className="text-gray-400 text-xs">Pose Score</div>
              <div className="text-white font-medium">
                {formatScore(currentPair.pose_score)}
              </div>
            </div>
          </div>

          {/* Progress slider */}
          <div>
            <div className="flex justify-between text-gray-400 text-xs mb-2">
              <span>Phase Progress</span>
              <span>{formatScore(phaseProgress)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={phaseProgress * 100}
              onChange={(e) => onProgressChange(Number(e.target.value) / 100)}
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-4
                [&::-webkit-slider-thumb]:h-4
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-blue-500
                [&::-webkit-slider-thumb]:cursor-pointer
                [&::-webkit-slider-thumb]:hover:bg-blue-400
                [&::-moz-range-thumb]:w-4
                [&::-moz-range-thumb]:h-4
                [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-blue-500
                [&::-moz-range-thumb]:border-0
                [&::-moz-range-thumb]:cursor-pointer"
            />
          </div>
        </div>
      )}

      {/* Overall timing summary */}
      <div className="flex justify-between text-sm">
        <div className="text-gray-400">
          Overall Timing Score: <span className="text-white font-medium">{formatScore(phaseMapping.overall_timing_score)}</span>
        </div>
        <div className="text-gray-400">
          Overall Pose Score: <span className="text-white font-medium">{formatScore(phaseMapping.overall_pose_score)}</span>
        </div>
      </div>
    </div>
  );
};

export default PhaseSyncTimeline;
